import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, MemoryStick, HardDrive, RefreshCw, ExternalLink, Database, ChevronRight, Power, Eraser, ShieldAlert, ShieldCheck } from "lucide-react";
import { api } from "../lib/api.js";
import { Button, Card, PageHeader, Spinner, StatusPill, Mono } from "../components/ui.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import { fmtMinor } from "../lib/money.js";

const gb = (b) => (b == null ? "—" : `${(b / 1e9).toFixed(1)} GB`);
const barColor = (v) => (v > 90 ? "var(--err)" : v > 75 ? "var(--warn)" : "var(--ok)");

// A remediation can be applied without working. The old badge said "auto-fixed" the
// moment one ran, which claimed a cure nobody had checked — and a red alert that says
// it is handled is one you stop reading.
//
// The check needs no new machinery: this list holds OPEN situations only, and every
// tick re-evaluates and resolves the ones that actually cleared. So a situation still
// on screen with auto_applied_at set is, by construction, one the fix did not cure.
// The only nuance is latency — a certificate takes a minute or two to issue after the
// restart that triggers it — so allow a grace window before calling it a failure.
const AUTOFIX_GRACE_MS = 5 * 60 * 1000;

/** null = never auto-applied · "applied" = ran, may still be taking effect · "stale" = ran and did not cure it. */
function autoFixState(situation, nowMs = Date.now()) {
  if (!situation.auto_applied_at) return null;
  const appliedMs = Date.parse(situation.auto_applied_at);
  if (!Number.isFinite(appliedMs)) return "applied";
  return nowMs - appliedMs < AUTOFIX_GRACE_MS ? "applied" : "stale";
}

// Fleet hygiene (INF-044). Shows only what is WRONG: servers that can be deleted
// with one API call, hosts nothing backs up, volumes no restic job covers. When the
// list is empty it says so in one line rather than rendering a wall of green ticks —
// this card exists to be noticed when it is not empty.
const SEV = {
  high:   { color: "var(--err)",  label: "critical" },
  medium: { color: "var(--warn)", label: "warning" },
  low:    { color: "var(--text-muted)", label: "tidy-up" },
};

function HygieneCard() {
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  const [showLow, setShowLow] = useState(false);

  useEffect(() => {
    let off = false;
    api.fleetHygiene()
      .then((d) => { if (!off) { setRep(d); setErr(""); } })
      .catch((e) => { if (!off) setErr(e.message || "Could not load hygiene"); });
    return () => { off = true; };
  }, []);

  // A failure to LOAD is reported, never silently rendered as "all clear" — an
  // empty hygiene card and a broken one must not look the same.
  if (err) {
    return (
      <Card>
        <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
          <ShieldAlert className="h-3.5 w-3.5" /> Fleet hygiene
        </div>
        <p className="text-xs" style={{ color: "var(--warn)" }}>Unavailable — {err}</p>
      </Card>
    );
  }
  if (!rep) return null;

  const findings = (rep.findings || []).filter((f) => showLow || f.severity !== "low");
  const lowHidden = (rep.findings || []).filter((f) => f.severity === "low").length;
  const clean = (rep.counts?.high || 0) + (rep.counts?.medium || 0) === 0;

  return (
    <Card>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
          {clean ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />}
          Fleet hygiene
        </div>
        {rep.counts?.high > 0 && <span className="pill" style={{ background: "var(--err)", color: "#fff", fontSize: 10, padding: "1px 6px" }}>{rep.counts.high} critical</span>}
        {rep.counts?.medium > 0 && <span className="pill" style={{ background: "var(--warn)", color: "#111", fontSize: 10, padding: "1px 6px" }}>{rep.counts.medium} warning</span>}
        <span className="ml-auto text-[11px]" style={{ color: "var(--text-muted)" }}>
          {rep.checked?.servers ?? 0} servers · {rep.checked?.volumes ?? 0} volumes
        </span>
      </div>

      {clean && findings.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--ok)" }}>
          Every server is delete-protected and backed up, and every volume is covered by a reporting restic job.
        </p>
      ) : (
        <div className="space-y-1.5">
          {findings.map((f, i) => (
            <div key={`${f.kind}-${f.subject}-${i}`} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs">
              <span className="font-semibold" style={{ color: SEV[f.severity]?.color, minWidth: 58 }}>
                {SEV[f.severity]?.label}
              </span>
              <Mono>{f.subject}</Mono>
              <span style={{ color: "var(--text)" }}>{f.detail}</span>
              {f.fix && (
                <code className="mono" style={{ color: "var(--text-muted)", fontSize: 11, opacity: 0.85 }} title="Suggested fix">
                  {f.fix}
                </code>
              )}
            </div>
          ))}
        </div>
      )}

      {lowHidden > 0 && (
        <button
          type="button"
          onClick={() => setShowLow((v) => !v)}
          className="mt-2 text-[11px]"
          style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--text-muted)" }}
        >
          {showLow ? "Hide" : `Show ${lowHidden} tidy-up item${lowHidden === 1 ? "" : "s"}`}
        </button>
      )}
    </Card>
  );
}

// Fleet-level counters. Leads with the numbers so the page answers "is anything wrong?"
// before you start reading rows.
// What KIND of thing a row is. Without this a database renders as a nameless row with
// a Restart button beside it and reads as a dead orphan — the exact misreading that
// nearly got twelve production databases deleted.
const KIND = {
  application: { label: "service", color: "var(--text-muted)" },
  database:    { label: "postgres", color: "var(--accent, #6ea8ff)" },
  redis:       { label: "redis", color: "var(--accent, #6ea8ff)" },
  unknown:     { label: "not in orchestrator", color: "var(--warn)" },
};
function KindTag({ kind }) {
  const k = KIND[kind] || KIND.unknown;
  return (
    <span
      className="ml-2 rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase"
      style={{ color: k.color, background: "var(--surface-2)", letterSpacing: ".03em" }}
      title={kind === "unknown" ? "A leftover metrics sample — the container no longer exists" : undefined}
    >
      {k.label}
    </span>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <Card>
      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="mt-1 text-2xl font-bold" style={{ color: tone || "var(--text)" }}>{value}</div>
      {sub && <div className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>{sub}</div>}
    </Card>
  );
}

function Gauge({ icon: Icon, label, pct, sub }) {
  return (
    <Card>
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--text-muted)" }}>
        <span className="inline-flex items-center gap-1"><Icon className="h-4 w-4" /> {label}</span>
        <span style={{ color: "var(--text)" }}>{pct == null ? "—" : `${pct}%`}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full" style={{ background: "var(--surface-2)" }}>
        {pct != null && <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor(pct) }} />}
      </div>
      {sub && <div className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>{sub}</div>}
    </Card>
  );
}

export default function Fleet() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [situations, setSituations] = useState([]);
  // ponytail: single shared dialog avoids N dialog states
  const [dialog, setDialog] = useState(null); // { situation } | null
  const [remBusy, setRemBusy] = useState(false);
  const [remErr, setRemErr] = useState("");
  const [note, setNote] = useState("");
  const [portals, setPortals] = useState([]);
  const [reboot, setReboot] = useState(null);       // { ip, name } awaiting confirmation
  const [rebootText, setRebootText] = useState(""); // must equal the host IP to arm the button
  const [rebootBusy, setRebootBusy] = useState(false);

  const load = () => {
    api.fleetOverview().then((d) => { setErr(""); setData(d); }).catch((e) => setErr(e.message || "Failed to load"));
    // ponytail: situations failure is isolated — fleet overview still renders
    api.situations().then((d) => setSituations(d.situations || [])).catch(() => setSituations([]));
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  // Portals are static per deploy — fetched once, and their absence is not an error.
  useEffect(() => { api.adminPortals().then((d) => setPortals(d.portals || [])).catch(() => setPortals([])); }, []);

  async function restart(uuid) {
    setBusy(uuid);
    try { await api.restartService(uuid); setTimeout(load, 2000); }
    catch (e) { setErr(e.message || "Restart failed"); }
    finally { setBusy(""); }
  }

  // Manual version of the clear-deploy-queue remediation, for when a deploy is wedged
  // but no situation has opened yet (the detector only fires after a stall threshold).
  async function clearQueue(uuid) {
    setBusy(uuid);
    setErr("");
    try {
      const r = await api.clearDeployQueue(uuid);
      setNote(r.cleared ? `Cleared ${r.cleared} stuck deploy${r.cleared === 1 ? "" : "s"}.` : "No stuck deploys on that service.");
      setTimeout(load, 1500);
    } catch (e) { setErr(e.message || "Clear queue failed"); }
    finally { setBusy(""); }
  }

  async function rebootHost() {
    if (!reboot) return;
    setRebootBusy(true);
    setErr("");
    try {
      const r = await api.rebootHost(reboot.ip);
      setNote(`Reboot sent to ${r.name || r.ip}. It will be unreachable for a minute or two.`);
      setReboot(null);
      setRebootText("");
      setTimeout(load, 5000);
    } catch (e) { setErr(e.message || "Reboot failed"); }
    finally { setRebootBusy(false); }
  }

  async function remediate() {
    if (!dialog) return;
    setRemBusy(true);
    setRemErr("");
    try {
      await api.remediateSituation(dialog.situation.id);
      setDialog(null);
      load();
    } catch (e) {
      setRemErr(e.message || "Remediation failed");
    } finally {
      setRemBusy(false);
    }
  }

  // ponytail: fall back to singleton so old API shape keeps working
  const hosts = data?.hosts ?? (data?.host ? [{ name: "primary", ...data.host }] : []);
  const hostLabel = (name) => name === "primary" ? "Primary host" : name;
  const hostGroups = Object.entries(hosts.reduce((groups, host) => {
    const type = host.providerType || "Other servers";
    (groups[type] ||= []).push(host);
    return groups;
  }, {})).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="page space-y-6">
      <PageHeader
        title="Fleet"
        subtitle="Host capacity and per-site usage"
        actions={portals.length ? (
          <div className="flex flex-wrap gap-2">
            {portals.map((p) => (
              <a key={p.key} href={p.url} target="_blank" rel="noreferrer" title={p.note}>
                <Button variant="ghost"><ExternalLink className="h-3.5 w-3.5" /> {p.label}</Button>
              </a>
            ))}
          </div>
        ) : null}
      />
      {err && <p className="text-sm" style={{ color: "var(--err)" }}>{err}</p>}
      {note && <p className="text-sm" style={{ color: "var(--ok)" }}>{note}</p>}

      <HygieneCard />

      {situations.length > 0 && (
        <Card>
          <div className="mb-2 text-xs font-semibold" style={{ color: "var(--text-muted)" }}>Situations</div>
          {remErr && <p className="mb-2 text-xs" style={{ color: "var(--err)" }}>{remErr}</p>}
          <div className="space-y-2">
            {situations.map((s) => {
              const auto = autoFixState(s);
              return (
              <div key={s.id} className="flex items-start justify-between gap-3 rounded p-2" style={{ background: "var(--surface-2)" }}>
                <div className="flex items-start gap-2">
                  <span className="mt-0.5 rounded px-1.5 py-0.5 text-xs font-semibold" style={{
                    background: s.severity === "crit" ? "var(--err)" : "var(--warn)",
                    color: "#fff",
                  }}>{s.severity}</span>
                  {auto === "applied" && (
                    <span className="mt-0.5 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-semibold"
                      title="An automatic fix was applied and may still be taking effect. This alert clears itself once the problem is actually gone."
                      style={{ color: "var(--ok)", background: "color-mix(in srgb, var(--ok) 15%, transparent)" }}>auto-applied</span>
                  )}
                  {auto === "stale" && (
                    <span className="mt-0.5 whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-semibold"
                      title="The automatic fix ran but the problem is still here, so something else is causing it. Worth a look."
                      style={{ color: "var(--warn)", background: "color-mix(in srgb, var(--warn) 15%, transparent)" }}>auto-fix didn&apos;t hold</span>
                  )}
                  <div>
                    <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{s.type}</div>
                    {s.detail && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{s.detail}</div>}
                  </div>
                </div>
                {/* Offer the button again once an auto-fix has demonstrably not worked — by
                    then the operator may have removed the real cause and want to retry. */}
                {s.suggested_remediation && auto !== "applied" && (
                  <Button variant="ghost" onClick={() => { setRemErr(""); setDialog({ situation: s }); }}>
                    {auto === "stale" ? "Retry fix" : "Apply fix"}
                  </Button>
                )}
              </div>
              );
            })}
          </div>
        </Card>
      )}

      {!data ? (
        <div className="flex h-40 items-center justify-center gap-2" style={{ color: "var(--text-muted)" }}><Spinner /> Loading…</div>
      ) : (
        <>
          {data.totals && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
              <Stat label="Services" value={data.totals.sites} sub={`across ${data.totals.hosts || 1} host${data.totals.hosts === 1 ? "" : "s"}`} />
              <Stat label="Running" value={data.totals.running} tone="var(--ok)" sub={data.totals.notRunning ? `${data.totals.notRunning} not running` : "all up"} />
              <Stat
                label="Unhealthy"
                value={data.totals.unhealthy}
                tone={data.totals.unhealthy ? "var(--err)" : "var(--ok)"}
                sub={data.totals.unhealthy ? "needs attention" : "none"}
              />
              <Stat
                label="Attached storage"
                value={`${data.totals.attachedDiskGb} GB`}
                sub={`${fmtMinor(data.totals.attachedDiskMonthlyPence, "gbp")}/mo billed`}
              />
              <Stat label="Container disk" value={gb(data.totals.containerDiskBytes)} sub="images + writable layers" />
            </div>
          )}

          {hostGroups.map(([type, groupedHosts]) => (
            <details key={type} open className="group overflow-hidden rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 [&::-webkit-details-marker]:hidden">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>{type}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>{groupedHosts.length} server{groupedHosts.length === 1 ? "" : "s"}</span>
                </span>
                <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" style={{ color: "var(--text-muted)" }} />
              </summary>
              <div className="space-y-5 border-t px-4 py-4" style={{ borderColor: "var(--border)" }}>
              {groupedHosts.map((h) => (
            <div key={h.name}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>{hostLabel(h.name)}</span>
                  {h.providerType && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ color: "var(--text-muted)", background: "var(--surface-2)" }}>{h.providerType}</span>}
                  {h.providerStatus && <StatusPill status={h.providerStatus} />}
                  {h.inventoryOnly && <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>Metrics pending</span>}
                </div>
                {h.ip && (
                  <Button variant="ghost" onClick={() => { setErr(""); setNote(""); setReboot({ ip: h.ip, name: hostLabel(h.name) }); }}>
                    <Power className="h-3.5 w-3.5" /> Reboot host
                  </Button>
                )}
              </div>
              {h.ip && <div className="-mt-1 mb-2 text-[11px]" style={{ color: "var(--text-muted)" }}>{h.ip}{h.privateIp ? ` · private ${h.privateIp}` : ""}</div>}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <Gauge icon={Cpu} label="CPU" pct={h.cpu} />
                <Gauge icon={MemoryStick} label="RAM" pct={h.mem?.pct} sub={h.mem ? `${gb(h.mem.used)} / ${gb(h.mem.total)}` : null} />
                <Gauge icon={HardDrive} label="Root disk" pct={h.diskRoot?.pct} sub={h.diskRoot ? `${gb(h.diskRoot.used)} / ${gb(h.diskRoot.total)}` : null} />
                {h.diskVolume && <Gauge icon={HardDrive} label="Docker volume" pct={h.diskVolume.pct} sub={`${gb(h.diskVolume.used)} / ${gb(h.diskVolume.total)}`} />}
              </div>
            </div>
              ))}
              </div>
            </details>
          ))}

          <Card>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: "var(--text-muted)" }} className="text-left text-xs">
                  <th className="p-2">Site</th><th className="p-2">Status</th><th className="p-2">CPU</th>
                  <th className="p-2">Memory</th><th className="p-2">Container disk</th>
                  <th className="p-2">Attached storage</th><th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {/* ponytail: sort by disk desc — highest consumers first */}
                {[...data.sites].sort((a, b) => (b.disk_bytes || 0) - (a.disk_bytes || 0)).map((s) => (
                  <tr key={s.uuid} style={{ borderTop: "1px solid var(--surface-2)" }}>
                    <td className="p-2">
                      {/* Straight through to the right management page — the fleet view is
                          where you notice a problem, so it should also be where you act on it.
                          A database is NOT a service: it links elsewhere, and an unresolved
                          uuid links nowhere at all rather than to a page that won't exist. */}
                      {s.kind === "unknown" ? (
                        <span className="inline-flex items-center gap-1" style={{ color: "var(--text-muted)" }} title="No longer present in the orchestrator — a leftover metrics sample, not a live resource">
                          <Mono>{s.uuid}</Mono>
                        </span>
                      ) : (
                        <Link
                          to={s.kind === "application" ? `/services/${s.uuid}` : `/databases/${s.uuid}`}
                          className="inline-flex items-center gap-1 hover:underline"
                          style={{ color: "var(--text)", fontWeight: 500 }}
                          title={s.kind === "application" ? "Open this service's settings, logs and deploys" : "Open this database"}
                        >
                          {s.name || s.uuid}
                          <ChevronRight className="h-3.5 w-3.5" style={{ color: "var(--text-muted)" }} />
                        </Link>
                      )}
                      <KindTag kind={s.kind} />
                      {s.domain && (
                        <a
                          href={`https://${s.domain}`}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-2 inline-flex items-center gap-1 text-xs hover:underline"
                          style={{ color: "var(--text-muted)" }}
                          title="Open the live site"
                        >
                          <ExternalLink className="h-3 w-3" />{s.domain}
                        </a>
                      )}
                    </td>
                    <td className="p-2">{s.status ? <StatusPill status={s.status} /> : "—"}</td>
                    <td className="p-2">{s.cpu_pct != null ? `${s.cpu_pct}%` : "—"}</td>
                    <td className="p-2">{gb(s.mem_bytes)}{s.mem_pct != null ? ` (${s.mem_pct}%)` : ""}</td>
                    <td className="p-2">{gb(s.disk_bytes)}</td>
                    <td className="p-2">
                      {s.diskGb > 0 ? (
                        <span
                          className="inline-flex items-center gap-1"
                          style={{ color: "var(--text)" }}
                          title={s.disks.map((d) => `${d.mountPath} — ${d.sizeGb} GB`).join("\n")}
                        >
                          <Database className="h-3.5 w-3.5" style={{ color: "var(--text-muted)" }} />
                          {s.diskGb} GB
                          <span style={{ color: "var(--text-muted)" }}>
                            · {fmtMinor(s.diskMonthlyPence, "gbp")}/mo
                            {s.disks.length > 1 ? ` · ${s.disks.length} disks` : ""}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)" }}>none</span>
                      )}
                    </td>
                    <td className="p-2">
                      {/* Actions are application-only. Restart posts to /api/services/:id and
                          a database isn't one; "clear queue" is meaningless for something
                          that never deploys. Showing them anyway would offer buttons that
                          fail — or worse, look like they apply to a database. */}
                      <div className="flex justify-end gap-1">
                        {s.kind === "application" ? (
                          <>
                            <Button
                              variant="ghost"
                              disabled={busy === s.uuid}
                              onClick={() => clearQueue(s.uuid)}
                              title="Unwedge a stuck deploy: fail its queued rows, drop hung build containers, nudge the worker"
                            >
                              <Eraser className="h-3.5 w-3.5" /> Clear queue
                            </Button>
                            <Button variant="ghost" disabled={busy === s.uuid} onClick={() => restart(s.uuid)}>
                              {busy === s.uuid ? <Spinner /> : <RefreshCw className="h-3.5 w-3.5" />} Restart
                            </Button>
                          </>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                            {s.kind === "unknown" ? "stale sample" : "manage on its own page"}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* Rebooting a host takes EVERY service on it down together, so this asks for the
          IP to be typed rather than offering a one-click button next to per-service
          actions. The server independently requires the same echo. */}
      {reboot && (
        <div
          onClick={rebootBusy ? undefined : () => setReboot(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "min(460px, 94vw)", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 20 }}
          >
            <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 600, color: "var(--text)" }}>Reboot {reboot.name}?</h3>
            <p style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)" }}>
              Every service on this host goes down until it comes back — usually a minute or two.
              {data?.sites?.length ? ` ${data.sites.length} services run across the fleet.` : ""}
              {" "}This is a graceful reboot, not a power cycle. Type <b style={{ color: "var(--text)" }}>{reboot.ip}</b> to confirm.
            </p>
            <input
              className="input"
              style={{ width: "100%", marginTop: 12, fontFamily: "var(--font-mono, monospace)" }}
              value={rebootText}
              onChange={(e) => setRebootText(e.target.value)}
              placeholder={reboot.ip}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <Button variant="ghost" onClick={() => { setReboot(null); setRebootText(""); }} disabled={rebootBusy}>Cancel</Button>
              <Button
                variant="danger"
                disabled={rebootBusy || rebootText.trim() !== reboot.ip}
                onClick={rebootHost}
              >
                {rebootBusy ? <Spinner /> : <Power className="h-3.5 w-3.5" />} Reboot host
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!dialog}
        title="Apply fix?"
        message={dialog ? `Run suggested remediation "${dialog.situation.suggested_remediation}" for ${dialog.situation.type}${dialog.situation.target ? ` on ${dialog.situation.target}` : ""}?` : ""}
        confirmLabel="Apply fix"
        busy={remBusy}
        onConfirm={remediate}
        onCancel={() => { setDialog(null); setRemErr(""); }}
      />
    </div>
  );
}
